import { useState, useEffect, useRef } from 'react';

export default function ShiftThreadHeader({
  feature,
  badge,
  badgeColor,
  tabLabel,
  tabColor,
  onTabClick,
  tabs,
  activeTab,
  onTabChange,
  isIncognito,
  onArtifacts,
  artifactsActive,
  onShare,
  actionSlot,
  onClose,
  onLeave,
}) {
  const [ghostHover, setGhostHover] = useState(false);
  const [artifactsHover, setArtifactsHover] = useState(false);
  const [shareHover, setShareHover] = useState(false);
  const [closeHover, setCloseHover] = useState(false);
  const [leaveHover, setLeaveHover] = useState(false);
  const [tabDropdownOpen, setTabDropdownOpen] = useState(false);
  const tabDropdownRef = useRef(null);

  // Click-outside closes the tab dropdown. Listener is only mounted
  // while the dropdown is open — detaches cleanly on close so we
  // aren't handling every click in the app.
  useEffect(() => {
    if (!tabDropdownOpen) return;
    function onDocMouseDown(e) {
      if (tabDropdownRef.current && !tabDropdownRef.current.contains(e.target)) {
        setTabDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [tabDropdownOpen]);

  const centerColor = tabColor || badgeColor || '#888888';

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr auto 1fr',
      alignItems: 'center',
      padding: '8px 20px',
      borderBottom: '0.5px solid #272736',
      background: '#07070a',
    }}>
      {/* LEFT: badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{
          background: 'rgba(200,169,110,0.08)',
          border: '1px solid rgba(200,169,110,0.25)',
          borderRadius: '6px',
          color: '#c8a96e',
          fontSize: '11px',
          fontWeight: 'bold',
          letterSpacing: '0.07em',
          padding: '3px 9px',
          textTransform: 'uppercase',
        }}>
          {badge}
        </span>
      </div>

      {/* CENTER: tab dropdown or single tab label */}
      {tabs && tabs.length > 0 ? (() => {
        const current = tabs.find(t => t.key === activeTab) || tabs[0];
        // Only show the non-active tabs in the dropdown — the active
        // one is already displayed as the header label above.
        const otherTabs = tabs.filter(t => t.key !== activeTab);
        return (
          <div ref={tabDropdownRef} style={{ position: 'relative' }}>
            <div
              onClick={() => setTabDropdownOpen(prev => !prev)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
            >
              <span style={{ color: current.color || '#c8a96e', fontSize: '14px', fontFamily: 'Georgia, serif', transition: 'color 0.15s' }}>
                {current.label}
              </span>
              <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                <path d="M2 3.5l3 3 3-3" stroke={current.color || '#888888'} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {tabDropdownOpen && otherTabs.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                background: '#141414', border: '1px solid #2a2a2a', borderRadius: 8,
                padding: '4px 0', zIndex: 100, minWidth: 130,
                boxShadow: '0 4px 16px rgba(0,0,0,0.4)', marginTop: 6,
              }}>
                {otherTabs.map(tab => (
                  <div key={tab.key}
                    onClick={() => { onTabChange?.(tab.key); setTabDropdownOpen(false); }}
                    style={{
                      padding: '8px 16px', color: '#888',
                      fontSize: 13, cursor: 'pointer', fontFamily: 'Georgia, serif',
                      transition: 'color 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = tab.color || '#c8a96e'}
                    onMouseLeave={e => e.currentTarget.style.color = '#888'}
                  >{tab.label}</div>
                ))}
              </div>
            )}
          </div>
        );
      })() : (
        <div
          onClick={onTabClick}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: onTabClick ? 'pointer' : 'default' }}
        >
          {onTabClick && (
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <path d="M2 4h12M2 8h12M2 12h12" stroke="#888888" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
          )}
          <span style={{ color: centerColor, fontSize: '14px', transition: 'color 0.15s' }}>
            {tabLabel}
          </span>
          {onTabClick && (
            <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
              <path d="M2 3.5l3 3 3-3" stroke="#888888" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
      )}

      {/* RIGHT: actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-end' }}>
        <div
          onClick={onArtifacts}
          onMouseOver={() => setArtifactsHover(true)}
          onMouseOut={() => setArtifactsHover(false)}
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={artifactsActive ? '#c8a96e' : artifactsHover ? '#7a9e82' : '#888888'} strokeWidth="1.3">
            <rect x="2" y="2" width="12" height="12" rx="2" />
            <path d="M10 2v12" />
          </svg>
        </div>
        {actionSlot || (
          <div
            onClick={onShare}
            onMouseOver={() => setShareHover(true)}
            onMouseOut={() => setShareHover(false)}
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
              <path d="M8 2v5M5.5 4.5L8 2l2.5 2.5" stroke={shareHover ? '#7a9e82' : '#888888'} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 14v-5M5.5 11.5L8 14l2.5-2.5" stroke={shareHover ? '#7a9e82' : '#888888'} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        )}
        {onClose && (
          <div
            onClick={onClose}
            onMouseOver={() => setCloseHover(true)}
            onMouseOut={() => setCloseHover(false)}
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1L11 11M11 1L1 11" stroke={closeHover ? '#c46050' : '#888888'} strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
        )}
        {onLeave && (
          <div
            onClick={onLeave}
            onMouseOver={() => setLeaveHover(true)}
            onMouseOut={() => setLeaveHover(false)}
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={leaveHover ? '#c46050' : '#888888'} strokeWidth="1.3" strokeLinecap="round">
              <path d="M6 2H4a2 2 0 00-2 2v8a2 2 0 002 2h2M10 12l4-4-4-4M14 8H6" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}